/**
 * Market Configuration
 *
 * This file defines all market-specific settings.
 *
 * Two modes of operation:
 * - ENV MODE (TCC): REACT_APP_MARKET is set → market determined at build time
 * - PATH MODE (OTC): REACT_APP_MARKET is NOT set → market determined from URL path
 *
 * To add a new market:
 * 1. Add a new entry to the MARKETS object below (include marketSlug for path mode)
 * 2. Create the logo file in public/images/
 * 3. For env-mode: Set REACT_APP_MARKET in the deployment's environment variables
 * 4. For path-mode: Just deploy — the slug in the URL resolves the market
 */

const MARKETS = {
  // Tri-Cities Central (Sam's market)
  tcc: {
    id: 'tcc',
    name: 'Tri-Cities Central',
    shortName: 'TCC',
    tagline: 'Happy Hours, Specials & Events in St. Charles, Geneva & Batavia',
    airtableBaseId: 'appa2S9rEPuUU2U11',

    // Branding
    logo: '/images/tcc-logo.jpg',
    headerLogo: '/images/tcc-logo.jpg',       // Logo for app header (same as login for TCC)
    logoAlt: 'Tri-Cities Central',
    headerTagline: 'Happy Hours, Specials & Events in St. Charles, Geneva & Batavia',
    headerLayout: 'inline',                    // 'inline' = logo left + text right, 'centered' = logo centered above tagline
    regionLabel: 'St. Charles, Geneva & Batavia',
    showRegionBar: false,                        // Region bar hidden on TCC; will be used as market switcher on OTC
    websiteUrl: 'https://www.tricitiescentral.com/',

    // Cities/Locations for WHERE filter
    cities: ['St. Charles', 'Geneva', 'Batavia', 'Campton Hills'],
    defaultCities: ['St. Charles', 'Geneva', 'Batavia', 'Campton Hills'], // Selected by default

    // Theme colors (can be used for CSS variables)
    theme: {
      primaryColor: '#2c5530',    // Dark green
      accentColor: '#4a7c59',     // Lighter green
      headerBg: '#2c5530',
      headerText: '#ffffff',
    },

    // Feature flags
    features: {
      venueOwnerAccounts: false,  // Sam manages all venues - no venue owner login
      hamburgerMenu: false,       // Public nav menu (Submit a Spot, Sign In, etc.)
    },
  },

  // Off the Clock Spots - Naperville (Kevin's market)
  'otc-naperville': {
    id: 'otc-naperville',
    name: 'Off the Clock Spots',
    shortName: 'OTC',
    tagline: 'Happy Hours, Daily Specials & Events in Naperville and the surrounding areas',
    airtableBaseId: 'app0tVZzcyaXRjyjW',

    // Branding
    logo: '/images/otc-logo-full.png',         // Full logo for login page
    headerLogo: '/images/OTC_Header_Logo.png', // Combined header image (clocks + logo + tagline)
    dashboardLogo: '/images/otc-simplified_logo.png', // Simplified logo for admin/venue dashboard header
    logoAlt: 'Off the Clock Spots',
    headerTagline: null,                        // Tagline is baked into the logo image
    headerLayout: 'centered',                  // Logo centered
    regionDescriptor: 'Happy Hours, Daily Specials & Event Listings',
    regionLabel: 'Naperville & Surrounding Communities',
    showRegionBar: true,                          // Region bar visible on OTC; future market switcher
    websiteUrl: null, // No external site yet
    marketSwitcherGroup: 'otc',              // Markets grouped in the region bar dropdown
    marketSlug: 'naperville',                 // Future path slug (/naperville)

    // Cities/Locations for WHERE filter
    cities: ['Naperville', 'Lisle', 'Downers Grove', 'Wheaton', 'Bolingbrook', 'Woodridge', 'Lombard'],
    defaultCities: ['Naperville', 'Lisle', 'Downers Grove', 'Wheaton', 'Bolingbrook', 'Woodridge', 'Lombard'],

    // Theme colors
    theme: {
      primaryColor: '#264653',    // Twilight Blue
      accentColor: '#3A2E5C',     // Deep Evening Purple
      headerBg: '#264653',
      headerText: '#ffffff',
    },

    features: {
      venueOwnerAccounts: true,   // Enable venue owner login/dashboard
      hamburgerMenu: true,        // Public nav menu (Submit a Spot, Sign In, etc.)
    },
  },

  // Off the Clock Spots - Fox Valley South (Kevin's market)
  'otc-fox-valley-south': {
    id: 'otc-fox-valley-south',
    name: 'Off the Clock Spots',
    shortName: 'OTC',
    tagline: 'Happy Hours, Daily Specials & Events in Aurora and the surrounding areas',
    airtableBaseId: 'appUy4h4TOIVjVw8S',

    // Branding (shared with OTC Naperville)
    logo: '/images/otc-logo-full.png',
    headerLogo: '/images/OTC_Header_Logo.png',
    dashboardLogo: '/images/otc-simplified_logo.png',
    logoAlt: 'Off the Clock Spots',
    headerTagline: null,
    headerLayout: 'centered',
    regionDescriptor: 'Happy Hours, Daily Specials & Event Listings',
    regionLabel: 'Aurora & Surrounding Communities',
    showRegionBar: true,
    websiteUrl: null,
    marketSwitcherGroup: 'otc',
    marketSlug: 'aurora',

    // Cities/Locations for WHERE filter
    cities: ['Aurora', 'North Aurora', 'Plainfield', 'Oswego', 'Montgomery', 'Sugar Grove', 'Yorkville'],
    defaultCities: ['Aurora', 'North Aurora', 'Plainfield', 'Oswego', 'Montgomery', 'Sugar Grove', 'Yorkville'],

    // Theme colors (same as OTC Naperville)
    theme: {
      primaryColor: '#264653',
      accentColor: '#3A2E5C',
      headerBg: '#264653',
      headerText: '#ffffff',
    },

    features: {
      venueOwnerAccounts: true,
      hamburgerMenu: true,        // Public nav menu (Submit a Spot, Sign In, etc.)
    },
  },

  // Template for new markets - copy and modify
  /*
  'otc-example': {
    id: 'otc-example',
    name: 'Off the Clock Spots',
    shortName: 'OTC',
    tagline: 'Happy Hours, Specials & Events in [Region]',
    airtableBaseId: 'appXXXXXXXXXXXXXX',  // Airtable base ID for this market

    logo: '/images/otc-logo.png',
    logoAlt: 'Off the Clock Spots',
    websiteUrl: null,

    cities: ['City1', 'City2', 'City3'],
    defaultCities: ['City1', 'City2', 'City3'],

    theme: {
      primaryColor: '#1a365d',
      accentColor: '#3182ce',
      headerBg: '#1a365d',
      headerText: '#ffffff',
    },

    features: {
      venueOwnerAccounts: true,   // Enable venue owner login/dashboard
    },
  },
  */
};

/**
 * Check if the app is running in path-based routing mode (OTC)
 * Path mode: REACT_APP_MARKET is NOT set → market comes from URL path
 * Env mode: REACT_APP_MARKET is set → market is baked in at build time (TCC)
 */
export const isPathMode = () => !process.env.REACT_APP_MARKET;

/**
 * Get the current market configuration (env-mode only)
 * Falls back to 'tcc' if no market is specified
 */
export const getMarketConfig = () => {
  const marketId = process.env.REACT_APP_MARKET || 'tcc';
  const config = MARKETS[marketId];

  if (!config) {
    console.warn(`Unknown market: ${marketId}, falling back to 'tcc'`);
    return MARKETS.tcc;
  }

  return config;
};

/**
 * Get market configuration by URL slug (path-mode)
 * Returns null if slug does not match any market
 */
export const getMarketConfigBySlug = (slug) => {
  if (!slug) return null;
  const found = Object.values(MARKETS).find(m => m.marketSlug === slug);
  return found || null;
};

/**
 * Default market slug for path-mode root redirect
 */
export const DEFAULT_MARKET_SLUG = 'naperville';

/**
 * Get all available markets (useful for admin/debugging)
 */
export const getAllMarkets = () => MARKETS;

/**
 * Check if a feature is enabled for the current market
 */
export const isFeatureEnabled = (featureName) => {
  const config = getMarketConfig();
  return config.features?.[featureName] ?? false;
};

/**
 * Market switcher URL mapping (env-mode only — used for cross-deployment navigation)
 * In path-mode, switcher uses React Router navigate() with slug paths instead
 */
export const MARKET_URLS = {
  'otc-naperville': 'https://otc-frontend.onrender.com',
};

/**
 * Get all markets in the same switcher group for the region bar dropdown
 * Returns [] for markets without a switcher group (e.g. TCC)
 * In path-mode: returns slug-based URLs for in-app navigation
 * In env-mode: returns full Render URLs for cross-deployment navigation
 */
export const getSwitcherMarkets = (currentMarketId) => {
  const current = MARKETS[currentMarketId];
  if (!current?.marketSwitcherGroup) return [];
  const pathMode = isPathMode();
  return Object.values(MARKETS)
    .filter(m => m.marketSwitcherGroup === current.marketSwitcherGroup)
    .map(m => ({
      id: m.id,
      slug: m.marketSlug,
      regionLabel: m.regionLabel,
      url: pathMode ? `/${m.marketSlug}` : MARKET_URLS[m.id],
      isCurrent: m.id === currentMarketId,
      isPathMode: pathMode,
    }));
};

export default MARKETS;
